import { useTranslation } from "react-i18next";
import type {
  MessageOriginName,
  PlatformTypeName,
  ReportMessageOptionsResponse,
  ReportMessageStatusBucketName,
} from "@multi-chat/shared";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { filterActiveReportOrigins } from "./active-report-origins.js";
import { ReportChannelSelect } from "./ReportChannelSelect.js";
import {
  originLabelKey,
  platformLabelKey,
  statusBucketLabelKey,
} from "./outgoing-report-presentation.js";

const ALL = "__ALL__";

export type OutgoingReportFilterValues = {
  platform?: PlatformTypeName;
  origin?: MessageOriginName;
  statusBucket?: ReportMessageStatusBucketName;
  endpointId?: number;
  campaignId?: number;
  template?: string;
};

export function countOutgoingActiveFilters(values: OutgoingReportFilterValues): number {
  let count = 0;
  if (values.platform) count += 1;
  if (values.origin) count += 1;
  if (values.statusBucket) count += 1;
  if (values.endpointId != null) count += 1;
  if (values.campaignId != null) count += 1;
  if (values.template) count += 1;
  return count;
}

export function OutgoingReportFilters({
  options,
  values,
  onChange,
  idPrefix = "reports-outgoing",
}: {
  options: ReportMessageOptionsResponse | null;
  values: OutgoingReportFilterValues;
  onChange: (patch: Partial<OutgoingReportFilterValues>) => void;
  idPrefix?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const platforms = options?.platforms ?? [];
  const origins = filterActiveReportOrigins(options?.origins ?? []);
  const statusBuckets = options?.statusBuckets ?? [];
  const endpoints = options?.endpoints ?? [];
  const campaigns = options?.campaigns ?? [];
  const templates = options?.templates ?? [];

  return (
    <div className="grid w-full min-w-0 gap-1.5" data-testid={`${idPrefix}-filters`}>
      <ReportChannelSelect
        value={values.platform}
        platforms={platforms}
        onChange={(platform) => onChange({ platform })}
        allLabel={t("reports.outgoing.filters.allPlatforms")}
        channelLabel={t("reports.outgoing.filters.platform")}
        labelForPlatform={(platform) => t(platformLabelKey(platform))}
        ariaLabel={t("reports.outgoing.filters.platform")}
        testId={`${idPrefix}-channel-filter`}
      />

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.outgoing.filters.origin")}</span>
        <SimpleSelect
          value={values.origin ?? ALL}
          onValueChange={(value) =>
            onChange({ origin: value === ALL ? undefined : (value as MessageOriginName) })
          }
          ariaLabel={t("reports.outgoing.filters.origin")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.outgoing.filters.allOrigins") },
            ...origins.map((origin) => ({
              value: origin,
              label: t(originLabelKey(origin)),
            })),
          ]}
        />
      </label>

      <label
        className="grid w-full min-w-0 gap-0.5 text-[0.75rem]"
        data-testid={`${idPrefix}-status-filter`}
      >
        <span className="text-muted">{t("reports.outgoing.filters.status")}</span>
        <SimpleSelect
          value={values.statusBucket ?? ALL}
          onValueChange={(value) =>
            onChange({
              statusBucket:
                value === ALL ? undefined : (value as ReportMessageStatusBucketName),
            })
          }
          ariaLabel={t("reports.outgoing.filters.status")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.outgoing.filters.allStatuses") },
            ...statusBuckets.map((bucket) => ({
              value: bucket,
              label: t(statusBucketLabelKey(bucket)),
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.outgoing.filters.endpoint")}</span>
        <SimpleSelect
          value={values.endpointId != null ? String(values.endpointId) : ALL}
          onValueChange={(value) =>
            onChange({
              endpointId: value === ALL ? undefined : Number(value),
            })
          }
          ariaLabel={t("reports.outgoing.filters.endpoint")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.outgoing.filters.allEndpoints") },
            ...endpoints.map((endpoint) => ({
              value: String(endpoint.endpointId),
              label: endpoint.secondaryLabel
                ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
                : endpoint.displayLabel,
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.outgoing.filters.campaign")}</span>
        <SimpleSelect
          value={values.campaignId != null ? String(values.campaignId) : ALL}
          onValueChange={(value) =>
            onChange({
              campaignId: value === ALL ? undefined : Number(value),
            })
          }
          ariaLabel={t("reports.outgoing.filters.campaign")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.outgoing.filters.allCampaigns") },
            ...campaigns.map((campaign) => ({
              value: String(campaign.campaignId),
              label: campaign.name,
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.outgoing.filters.template")}</span>
        <SimpleSelect
          value={values.template ?? ALL}
          onValueChange={(value) =>
            onChange({ template: value === ALL ? undefined : value })
          }
          ariaLabel={t("reports.outgoing.filters.template")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.outgoing.filters.allTemplates") },
            ...templates.map((template) => ({
              value: template.name,
              label: template.language
                ? `${template.name} (${template.language})`
                : template.name,
            })),
          ]}
        />
      </label>
    </div>
  );
}
